import React from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';

const PatternRecognitionPanel = ({ patterns }) => {
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-50';
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  const getTrendIcon = (insight) => {
    if (insight?.includes('increasing')) return <TrendingUp className="w-5 h-5 text-red-500" />;
    if (insight?.includes('decreasing')) return <TrendingDown className="w-5 h-5 text-green-500" />;
    return <Minus className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-purple-100 rounded-lg">
          <TrendingUp className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Pattern Recognition</h2>
          <p className="text-sm text-gray-600">AI-detected patterns in your symptom data</p>
        </div>
      </div>
      <div className="space-y-4">
        {patterns?.map((pattern, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {getTrendIcon(pattern?.insight)}
                <div>
                  <h3 className="font-semibold text-gray-900 capitalize">
                    {pattern?.type?.replace('_', ' ')}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{pattern?.insight}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getConfidenceColor(pattern?.confidence)}`}>
                {(pattern?.confidence * 100)?.toFixed(0)}% confidence
              </span>
            </div>

            {pattern?.type === 'frequency' && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Symptom Frequency</h4>
                <div className="space-y-2">
                  {Object.entries(pattern?.data || {})
                    ?.sort((a, b) => b?.[1] - a?.[1])
                    ?.slice(0, 5)
                    ?.map(([symptom, count]) => (
                      <div key={symptom} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{symptom}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full"
                              style={{ width: `${(count / Math.max(...Object.values(pattern?.data))) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {pattern?.type === 'timing' && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Time of Day Distribution</h4>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(pattern?.data || {})?.map(([time, count]) => (
                    <div key={time} className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-purple-600">{count}</div>
                      <div className="text-xs text-gray-600 capitalize mt-1">{time}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pattern?.type === 'severity_trend' && pattern?.data?.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Severity Over Time</h4>
                <div className="h-24 flex items-end gap-1">
                  {pattern?.data?.slice(-14)?.map((point, idx) => (
                    <div
                      key={idx}
                      className="flex-1 bg-purple-400 rounded-t hover:bg-purple-600 transition-colors"
                      style={{ height: `${(point?.severity / 10) * 100}%` }}
                      title={`Severity: ${point?.severity}`}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>14 days ago</span>
                  <span>Today</span>
                </div>
              </div>
            )}
          </div>
        ))}

        {(!patterns || patterns?.length === 0) && (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Not enough data to detect patterns yet</p>
            <p className="text-sm text-gray-500 mt-1">Keep tracking symptoms to unlock insights</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatternRecognitionPanel;